/**
 * @fileoverview Universal-search result kind metadata
 *
 * Maps each `kind` returned by the backend `universalSearch` query
 * to its label, icon, and TanStack-Router route. Centralised so the
 * typeahead bar, chat citations, and `@`-mention picker render
 * consistently.
 *
 * Adding a new searchable entity:
 * 1. Extend `universalSearchKindValidator` in `convex/agents/universal/search.ts`.
 * 2. Add a scanner function in the same file.
 * 3. Add an entry below — the UI is data-driven from this table.
 */

import {
  Briefcase,
  FileText,
  Users,
  Building2,
  Coins,
  Banknote,
  Shield,
  HandCoins,
  Receipt,
  UserSquare2,
  Contact,
  Flag,
  Scale,
  type LucideIcon,
} from "lucide-react";
import {
  CATEGORY_CONFIG,
  CATEGORY_LABELS,
  LEGACY_CATEGORY_MAP,
} from "@cvx/lib/documentCategories";
import { JOB_STATUS_LABELS, JOB_STATUS_VARIANTS } from "@/lib/job-statuses";
import type { BadgeColor, BadgeProps } from "@/ui/primitives/badge";

export type SearchResultKind =
  | "job"
  | "document"
  | "contact"
  | "firm_contact"
  | "asset"
  | "related_party"
  | "liability"
  | "security_interest"
  | "loan"
  | "ato_sro"
  | "employee"
  | "flagged_transaction"
  | "pod_claim";

export interface SearchResultKindMeta {
  label: string;
  icon: LucideIcon;
  /** Tab-scoped colour class for the kind badge. */
  badgeClass: string;
  /**
   * Builds a TanStack-Router compatible link target for a result row.
   * Returns `null` if the kind is not navigable (e.g. raw findings
   * without a detail panel yet).
   */
  buildTo: (args: { id: string; jobId?: string; orgId?: string }) => {
    to: string;
    params?: Record<string, string>;
    search?: Record<string, string>;
  } | null;
}

const NO_NAV: SearchResultKindMeta["buildTo"] = () => null;

export const SEARCH_RESULT_KINDS: Record<
  SearchResultKind,
  SearchResultKindMeta
> = {
  job: {
    label: "Job",
    icon: Briefcase,
    badgeClass: "bg-primary/10 text-primary border-primary/20",
    buildTo: ({ jobId, id }) => {
      const target = jobId ?? id;
      return {
        to: "/dashboard/jobs/$jobId/plan",
        params: { jobId: target },
      };
    },
  },
  document: {
    label: "Document",
    icon: FileText,
    badgeClass:
      "bg-blue-500/10 text-blue-700 border-blue-500/20 dark:text-blue-300",
    buildTo: ({ jobId, id }) => {
      if (!jobId) return null;
      return {
        to: "/dashboard/jobs/$jobId/gather",
        params: { jobId },
        search: { documentId: id },
      };
    },
  },
  contact: {
    label: "Contact",
    icon: Users,
    badgeClass:
      "bg-emerald-500/10 text-emerald-700 border-emerald-500/20 dark:text-emerald-300",
    buildTo: ({ jobId, id }) => {
      if (!jobId) return null;
      return {
        to: "/dashboard/jobs/$jobId/contacts",
        params: { jobId },
        search: { contactId: id },
      };
    },
  },
  firm_contact: {
    label: "Firm contact",
    icon: Contact,
    badgeClass:
      "bg-emerald-500/10 text-emerald-700 border-emerald-500/20 dark:text-emerald-300",
    buildTo: ({ id }) => ({
      to: "/dashboard/firm-contacts",
      search: { contactId: id },
    }),
  },
  asset: {
    label: "Asset",
    icon: Coins,
    badgeClass:
      "bg-amber-500/10 text-amber-700 border-amber-500/20 dark:text-amber-300",
    buildTo: ({ jobId }) => {
      if (!jobId) return null;
      return {
        to: "/dashboard/jobs/$jobId/summaries/$agent",
        params: { jobId, agent: "assets" },
      };
    },
  },
  related_party: {
    label: "Related party",
    icon: Building2,
    badgeClass:
      "bg-violet-500/10 text-violet-700 border-violet-500/20 dark:text-violet-300",
    buildTo: ({ jobId }) => {
      if (!jobId) return null;
      return {
        to: "/dashboard/jobs/$jobId/summaries/$agent",
        params: { jobId, agent: "related-parties" },
      };
    },
  },
  liability: {
    label: "Liability",
    icon: HandCoins,
    badgeClass:
      "bg-rose-500/10 text-rose-700 border-rose-500/20 dark:text-rose-300",
    buildTo: ({ jobId }) => {
      if (!jobId) return null;
      return {
        to: "/dashboard/jobs/$jobId/summaries/$agent",
        params: { jobId, agent: "creditors" },
      };
    },
  },
  security_interest: {
    label: "Security interest",
    icon: Shield,
    badgeClass:
      "bg-cyan-500/10 text-cyan-700 border-cyan-500/20 dark:text-cyan-300",
    buildTo: ({ jobId }) => {
      if (!jobId) return null;
      return {
        to: "/dashboard/jobs/$jobId/summaries/$agent",
        params: { jobId, agent: "security-interests" },
      };
    },
  },
  loan: {
    label: "Loan",
    icon: Banknote,
    badgeClass:
      "bg-sky-500/10 text-sky-700 border-sky-500/20 dark:text-sky-300",
    buildTo: ({ jobId }) => {
      if (!jobId) return null;
      return {
        to: "/dashboard/jobs/$jobId/summaries/$agent",
        params: { jobId, agent: "loans" },
      };
    },
  },
  ato_sro: {
    label: "ATO/SRO",
    icon: Receipt,
    badgeClass:
      "bg-orange-500/10 text-orange-700 border-orange-500/20 dark:text-orange-300",
    buildTo: ({ jobId }) => {
      if (!jobId) return null;
      return {
        to: "/dashboard/jobs/$jobId/summaries/$agent",
        params: { jobId, agent: "ato-sro" },
      };
    },
  },
  employee: {
    label: "Employee",
    icon: UserSquare2,
    badgeClass:
      "bg-teal-500/10 text-teal-700 border-teal-500/20 dark:text-teal-300",
    buildTo: ({ jobId }) => {
      if (!jobId) return null;
      return {
        to: "/dashboard/jobs/$jobId/summaries/$agent",
        params: { jobId, agent: "employment" },
      };
    },
  },
  flagged_transaction: {
    label: "Flagged transaction",
    icon: Flag,
    badgeClass:
      "bg-red-500/10 text-red-700 border-red-500/20 dark:text-red-300",
    buildTo: ({ jobId }) => {
      if (!jobId) return null;
      return {
        to: "/dashboard/jobs/$jobId/review/$agent",
        params: { jobId, agent: "flagged-transactions" },
      };
    },
  },
  // Cited by generated reports when a statement rests on a creditor's proof of
  // debt or its adjudication. `claimId` opens that claim's panel directly.
  pod_claim: {
    label: "Proof of debt",
    icon: Scale,
    badgeClass:
      "bg-indigo-500/10 text-indigo-700 border-indigo-500/20 dark:text-indigo-300",
    buildTo: ({ jobId, id }) => {
      if (!jobId) return null;
      return {
        to: "/dashboard/jobs/$jobId/recoveries/$section",
        params: { jobId, section: "proof-of-debt" },
        search: { claimId: id },
      };
    },
  },
};

/**
 * Maps table names and other LLM-emitted variations to canonical kind
 * keys. The agent sometimes uses the raw Convex table name (e.g.
 * `securityInterestEntries`) instead of the standardised kind.
 */
const KIND_ALIASES: Record<string, SearchResultKind> = {
  documents: "document",
  assetentries: "asset",
  relatedpartyentries: "related_party",
  liabilityentries: "liability",
  securityinterestentries: "security_interest",
  loanentries: "loan",
  employeeentries: "employee",
  atosroentries: "ato_sro",
  flaggedtransactions: "flagged_transaction",
  podclaims: "pod_claim",
  proofofdebt: "pod_claim",
  jobcontacts: "contact",
  firmcontacts: "firm_contact",
  jobs: "job",
};

function normalizeKind(kind: string): string {
  const lower = kind.toLowerCase().replace(/[\s_-]/g, "");
  return KIND_ALIASES[lower] ?? kind;
}

/** Safe lookup that falls back to a no-op for unknown kinds. */
function isSearchResultKind(kind: string): kind is SearchResultKind {
  return Object.prototype.hasOwnProperty.call(SEARCH_RESULT_KINDS, kind);
}

/**
 * Resolve a possibly-aliased kind string (e.g. a raw table name) to its
 * canonical {@link SearchResultKind}, or `null` when unrecognised. Lets callers
 * special-case a kind (e.g. open a POD claim in-place) without duplicating the
 * alias table.
 */
export function canonicalSearchKind(kind: string): SearchResultKind | null {
  const normalized = normalizeKind(kind);
  return isSearchResultKind(normalized) ? normalized : null;
}

/**
 * Look up display/navigation metadata for a search-result kind.
 *
 * @param kind - The result's kind string as returned by the universal search
 *   backend (untrusted at this boundary).
 * @returns The registered `KindMeta` for known kinds; the generic fallback
 *   meta for anything unrecognised.
 */ export function getKindMeta(kind: string): SearchResultKindMeta {
  const normalized = normalizeKind(kind);
  if (isSearchResultKind(normalized)) {
    return SEARCH_RESULT_KINDS[normalized];
  }
  return {
    label: kind,
    icon: FileText,
    badgeClass: "bg-muted text-muted-foreground border-border",
    buildTo: NO_NAV,
  };
}

// ---------------------------------------------------------------------------
// Per-row badge resolution
// ---------------------------------------------------------------------------

/**
 * Maps the colour string used by `CATEGORY_CONFIG` (Tailwind colour
 * stems like "slate", "cyan", "fuchsia") onto the curated palette the
 * shared `Badge` primitive understands (`BadgeColor`). Categories that
 * don't have a direct match collapse onto a visually-close colour.
 */
const CATEGORY_COLOUR_TO_BADGE: Record<string, BadgeColor> = {
  slate: "gray",
  neutral: "gray",
  gray: "gray",
  blue: "blue",
  sky: "sky",
  cyan: "sky",
  teal: "teal",
  emerald: "emerald",
  green: "green",
  lime: "green",
  amber: "amber",
  yellow: "amber",
  orange: "orange",
  red: "red",
  rose: "red",
  pink: "pink",
  fuchsia: "pink",
  purple: "purple",
  violet: "violet",
  indigo: "indigo",
};

export interface ResolvedRowBadge {
  /** Display text for the badge. */
  label: string;
  /** Color-aware (`color`/`soft`) badge config, or variant-only. */
  color?: BadgeColor;
  variant?: BadgeProps["variant"];
}

/**
 * Resolves the per-row badge for a search result. Documents get a
 * coloured category badge, jobs get a status badge, and other kinds
 * fall back to a muted subtitle pill (or none when no subtitle).
 */
export function resolveResultBadge(args: {
  kind: string;
  subtitle?: string;
  categoryKey?: string;
  statusKey?: string;
}): ResolvedRowBadge | null {
  if (args.kind === "document" && args.categoryKey) {
    const normalised =
      LEGACY_CATEGORY_MAP[args.categoryKey] ?? args.categoryKey;
    const cfg = CATEGORY_CONFIG[normalised];
    if (cfg) {
      return {
        label: cfg.label,
        color: CATEGORY_COLOUR_TO_BADGE[cfg.colour] ?? "gray",
        variant: "soft",
      };
    }
    return {
      label: CATEGORY_LABELS[normalised] ?? normalised,
      color: "gray",
      variant: "soft",
    };
  }

  if (args.kind === "job" && args.statusKey) {
    const label = JOB_STATUS_LABELS[args.statusKey] ?? args.statusKey;
    const variant = JOB_STATUS_VARIANTS[args.statusKey] ?? "outline";
    return { label, variant };
  }

  if (args.subtitle && args.subtitle.trim().length > 0) {
    return { label: args.subtitle, variant: "outline" };
  }

  return null;
}
